import { useMemo, useState } from "react";
import { routes } from "@/router";
import { buildCurrentProductDefinition } from "@/utils/buildCurrentDynamicFormDefinition";
import { buildDefaultValues, formatAnswers } from "@/utils/dynamicForm.util";
import { PredictiveProductAnswerDto } from "@advanced-sales-technologies/backend-client/models/PredictiveProductAnswerDto";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FormValues } from "@/types/DynamicForm.type";
import { useGetOrgaInfo } from "@/hooks/api/organisation/useGetOrgaInfo";
import { useCreateProduct } from "@/hooks/api/predictive/product/useCreateProduct";
import { useGetProductQuestions } from "@/hooks/api/predictive/product/useGetProductQuestions";
import Spinner from "@/components/atoms/Spinner";
import DynamicForm from "@/components/molecules/form/DynamicForm";
import PredictiveLeadsHeader from "@/components/organisms/predictive/PredictiveLeadsHeader";
import ProductCreatedFeaturedText from "@/components/organisms/predictive/ProductCreatedFeaturedText";
import { productFormDefinition, ProductFormValues } from "@/components/organisms/predictive/productFormDefinition";

const CreateProduct = () => {
  const { t } = useTranslation();
  const { data: questions, isLoading: questionsLoading } = useGetProductQuestions();
  const { createProduct, isPending } = useCreateProduct();
  const navigate = useNavigate();
  const { data: organisation } = useGetOrgaInfo();
  const [showFirstProductMessage, setShowFirstProductMessage] = useState(false);
  const [createdProductId, setCreatedProductId] = useState<string | null>(null);

  const currentProductDefinition = useMemo(
    () =>
      buildCurrentProductDefinition(productFormDefinition(t), questions, {
        includeName: true,
        includeHeaders: true,
      }),
    [productFormDefinition, questions],
  );

  const defaultValues = useMemo(() => buildDefaultValues(currentProductDefinition), [currentProductDefinition]);

  const handleSubmit = async (data: ProductFormValues) => {
    const { name, ...answers } = data;
    const formatted = formatAnswers(
      currentProductDefinition,
      answers as FormValues<typeof currentProductDefinition>,
    ) as PredictiveProductAnswerDto[];

    createProduct(
      {
        name: name as string,
        answers: formatted,
        displayToast: true,
      },
      {
        onSuccess: (data) => {
          setCreatedProductId(data.id);

          if (organisation?.has_ai_product) navigate(routes.predictive);
          else setShowFirstProductMessage(true);
        },
      },
    );
  };

  return (
    <div className={"h-screen w-full overflow-y-auto px-12"}>
      <PredictiveLeadsHeader showIntroButton />

      {showFirstProductMessage ? (
        <div className={"flex h-full max-h-screen items-center justify-center"}>
          <ProductCreatedFeaturedText productId={createdProductId!} />
        </div>
      ) : questionsLoading ? (
        <div className={"flex h-full items-center justify-center"}>
          <Spinner />
        </div>
      ) : (
        <div className={"mx-auto mt-12 max-w-xl"}>
          <DynamicForm
            config={currentProductDefinition}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            isPending={isPending}
            submitBtnText={t("predictive.product.create")}
          />
        </div>
      )}
    </div>
  );
};

export default CreateProduct;
